/**
 * AutoGradingSection
 *
 * Shown inside ProjectSettingsStep when the instructor toggles "Auto Grading".
 * Lets them:
 *  1. Toggle auto_grading_enabled
 *  2. Choose evaluation_type  (test_script | exact_match | ai_assisted)
 *  3. Upload a pytest test script → stored as evaluation_script_s3_key on the project
 *
 * The component calls the /api/s3/upload-project-dataset endpoint (multipart) and
 * stores the returned S3 key in projectData so the parent form can submit it.
 */

import React, { useRef, useState, useEffect } from "react";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Badge } from "./ui/badge";
import {
  Bot,
  Upload,
  FileCode2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  Sparkles,
} from "lucide-react";
import { api } from "../services/AxiosService";
import getBITSEndApiMap from "../apiMap";
import { toast } from "sonner";

interface AutoGradingSectionProps {
  projectData: any;
  setProjectData: React.Dispatch<React.SetStateAction<any>>;
  /** projectId is needed for the S3 upload (multipart form) */
  projectId?: string;
}

type EvalType = "ai_assisted" | "evaluation_script";

type UploadState = "idle" | "uploading" | "success" | "error";

export const AutoGradingSection: React.FC<AutoGradingSectionProps> = ({
  projectData,
  setProjectData,
  projectId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [uploadError, setUploadError] = useState<string>("");

  const autoGradingEnabled = !!projectData?.auto_grading_enabled;
  const evalType: EvalType = projectData?.evaluation_type || "ai_assisted";
  const existingKey: string = projectData?.evaluation_script_s3_key || "";

  const [generatingScript, setGeneratingScript] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string | null>(null);

  const handleToggle = (checked: boolean) => {
    setProjectData((prev: any) => ({
      ...prev,
      auto_grading_enabled: checked,
      evaluation_type: checked ? (prev.evaluation_type || "ai_assisted") : prev.evaluation_type,
    }));
  };

  // Ensure default evaluation_type is set in projectData if auto grading is enabled.
  // Also migrate legacy "ai_review" value to the current default "ai_assisted".
  const VALID_EVAL_TYPES: EvalType[] = ["ai_assisted", "evaluation_script"];
  useEffect(() => {
    if (autoGradingEnabled) {
      const currentType = projectData?.evaluation_type;
      if (!currentType || !VALID_EVAL_TYPES.includes(currentType)) {
        setProjectData((prev: any) => ({
          ...prev,
          evaluation_type: "ai_assisted"
        }));
      }
    }
  }, [autoGradingEnabled, projectData?.evaluation_type, setProjectData]);

  const handleEvalTypeChange = (value: EvalType) => {
    setProjectData((prev: any) => ({
      ...prev,
      evaluation_type: value,
    }));
  };

  const handleGenerateScript = async () => {
    if (!projectId) return;
    setGeneratingScript(true);
    setGenerationStatus("requesting");
    
    try {
      const url = getBITSEndApiMap("generateEvaluationScript" as any).replace(":id", projectId);
      await api.post(url);
      setGenerationStatus("generating");
      toast.info("AI evaluation script generation started...");
      
      // Start polling
      pollGenerationStatus();
    } catch (err: any) {
      setGeneratingScript(false);
      setGenerationStatus(null);
      toast.error("Failed to start AI generation: " + (err.response?.data?.message || err.message));
    }
  };

  const pollGenerationStatus = async () => {
    if (!projectId) return;
    
    const poll = async () => {
      try {
        const url = getBITSEndApiMap("getEvaluationScriptStatus" as any).replace(":id", projectId);
        const resp = await api.get(url);
        const { status, message } = resp.data as any;
        
        if (status === "completed") {
          setGeneratingScript(false);
          setGenerationStatus(null);
          toast.success("AI evaluation script generated successfully!");
          
          const projectUrl = getBITSEndApiMap("getProjectById" as any) + `/${projectId}`;
          const projectResp = await api.get(projectUrl);
          if (projectResp.data?.data?.evaluation_script_s3_key) {
             setProjectData((prev: any) => ({
               ...prev,
               evaluation_script_s3_key: projectResp.data.data.evaluation_script_s3_key
             }));
          }
        } else if (status === "failed") {
          setGeneratingScript(false);
          setGenerationStatus(null);
          toast.error("AI generation failed: " + message);
        } else {
          // Keep polling
          setTimeout(poll, 3000);
        }
      } catch (err) {
        console.error("Polling error", err);
        setTimeout(poll, 5000);
      }
    };
    
    setTimeout(poll, 2000);
  };

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".py")) {
      toast.error("Only Python (.py) test scripts are supported.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Test script must be smaller than 5 MB.");
      return;
    }
    if (!projectId) {
      toast.error("Save the project first before uploading a test script.");
      return;
    }

    setUploadState("uploading");
    setUploadError("");

    try {
      const url = getBITSEndApiMap("uploadEvaluationScript" as any).replace(":id", projectId);
      
      const reader = new FileReader();
      reader.onload = async (event) => {
        const content = event.target?.result as string;
        try {
          const response = await api.put(url, { scriptContent: content });
          const key = (response.data as any)?.data?.key;
          if (!key) throw new Error("No S3 key returned from update");

          setProjectData((prev: any) => ({
            ...prev,
            evaluation_script_s3_key: key,
          }));
          setUploadState("success");
          toast.success(`Test script "${file.name}" updated successfully.`);
        } catch (err: any) {
          setUploadError(err.message);
          setUploadState("error");
          toast.error("Failed to update test script: " + err.message);
        }
      };
      reader.readAsText(file);

    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Upload failed. Please try again.";
      setUploadError(msg);
      setUploadState("error");
      toast.error(msg);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveScript = () => {
    setProjectData((prev: any) => ({
      ...prev,
      evaluation_script_s3_key: null,
    }));
    setUploadState("idle");
    setUploadError("");
  };

  const scriptFilename = existingKey
    ? existingKey.split("/").pop() || existingKey
    : null;

  return (
    <div className="space-y-5">
      {/* ── Toggle ── */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <Label className="text-sm font-medium leading-none">
              Enable Auto Grading
            </Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Automatically grade student Jupyter notebooks via AI or a test script
            </p>
          </div>
        </div>
        <Switch checked={autoGradingEnabled} onCheckedChange={handleToggle} />
      </div>

      {/* ── Config panel (only when enabled) ── */}
      {autoGradingEnabled && (
        <div className="pl-2 space-y-5 border-l-2 border-blue-200">
          {/* Evaluation type - HIDDEN for now */}
          {false && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Evaluation Strategy</Label>
              <Select value={evalType} onValueChange={handleEvalTypeChange}>
                <SelectTrigger className="w-64 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ai_assisted">
                    AI-Assisted Review
                  </SelectItem>
                  <SelectItem value="evaluation_script">
                    Evaluation script
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Script management - Visible if a script exists (allow deletion) or if specifically in script mode (legacy) */}
          {(existingKey || (false && evalType === "evaluation_script")) && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Evaluation Test Script (.py)
              </Label>

              {existingKey && uploadState !== "uploading" ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-green-900 truncate">
                      {scriptFilename}
                    </p>
                    <p className="text-xs text-green-700">
                      Script uploaded and ready
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs border-green-300 text-green-700 hover:bg-green-100"
                    >
                      <Upload className="h-3 w-3 mr-1" />
                      Replace
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveScript}
                      className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() =>
                    uploadState !== "uploading" && fileInputRef.current?.click()
                  }
                  className={`
                    relative flex flex-col items-center justify-center gap-2 p-6 
                    border-2 border-dashed rounded-lg cursor-pointer transition-colors
                    ${
                      uploadState === "uploading"
                        ? "border-blue-300 bg-blue-50 cursor-not-allowed"
                        : uploadState === "error"
                          ? "border-red-300 bg-red-50 hover:bg-red-100"
                          : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"
                    }
                  `}
                >
                  {uploadState === "uploading" ? (
                    <>
                      <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                      <p className="text-sm text-blue-600 font-medium">
                        Uploading…
                      </p>
                    </>
                  ) : uploadState === "error" ? (
                    <>
                      <AlertCircle className="h-8 w-8 text-red-500" />
                      <p className="text-sm text-red-600 font-medium">
                        Upload failed
                      </p>
                      <p className="text-xs text-red-500 text-center max-w-xs">
                        {uploadError}
                      </p>
                    </>
                  ) : (
                    <>
                      <FileCode2 className="h-8 w-8 text-gray-400" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-gray-700">
                          Upload test script
                        </p>
                        <p className="text-xs text-gray-500">
                          Python pytest file (.py), max 5 MB
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".py"
                className="hidden"
                onChange={handleFileSelect}
              />

              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-start gap-3 mt-4">
                <Sparkles className="h-5 w-5 text-purple-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-purple-900">
                    AI Script Generation Available
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3 bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200"
                    onClick={handleGenerateScript}
                    disabled={generatingScript || !projectId}
                  >
                    {generatingScript ? "Generating..." : "Generate with AI"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* AI review info always visible when enabled */}
          <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3 mt-4">
            <Bot className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-indigo-900">
                AI-Assisted Review Flow
              </p>
              <p className="text-xs text-indigo-700 mt-1">
                Student notebooks will be executed directly in a Docker container, and their outputs along with the code/markdown content will be analyzed by AI for grading and feedback.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
